import { SKY_WORLD, SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { skyAuthoredPoseContact } from '../../../packages/domain/src/115-sky-authored-pose.mjs';

/** The encounter's actionable source point: current gun tell, the actual copper
 * horn/cable knot, or the surviving exposed reactor during the repair window. */
export function skyTwinViewObjective(state) {
  const t = state.boss?.twins;
  if (!t) return null;
  if (t.phase === 'transfer')
    return { kind: t.tether.knot ? 'knot' : 'horn', point: t.tether.knot ?? t.horns[t.donor] };
  if (t.tells?.length) return { kind: 'warning', point: t.tells[0].from };
  const member = t.members.findIndex((m) => m.hp > 0);
  return {
    kind: 'reactor',
    member: Math.max(0, member),
    point: t.members[Math.max(0, member)].pose.sockets.reactor_target,
  };
}

/** A fixed physical shipyard shoulder never retreats to fit two entire28m
 * craft. The source objective and original1.8m pilot own the angular envelope;
 * companion/cable context is included when it fits the same bounded lens. */
export function skyTwinCloseCameraFrame(base, state, aspect, pointFov) {
  const t = state.boss.twins,
    objective = skyTwinViewObjective(state).point;
  const pilot = { x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ };
  const eye = { ...pilot, y: pilot.y + 0.7 };
  let position = { x: pilot.x + (aspect < 1 ? 0.45 : 0.9), y: pilot.y + 1.5, z: pilot.z + 5.85 };
  const blocked = skyAuthoredPoseContact(t.gantry.authoredPose, eye, position, null, 0.18);
  if (blocked) {
    const f = Math.max(0, blocked.fraction - 0.04);
    position = Object.fromEntries(
      ['x', 'y', 'z'].map((a) => [a, eye[a] + (position[a] - eye[a]) * f]),
    );
  }
  const sole = pilot.y - SKY_WORLD.pilotAimHeight;
  const points = [
    { ...pilot, y: sole },
    { ...pilot, y: sole + SKY_WORLD_ASSETS.pilot.height },
    objective,
  ];
  const angles = points.map((p) => Math.atan2(p.x - position.x, position.z - p.z));
  const yaw = (Math.min(...angles) + Math.max(...angles)) / 2;
  const pitches = points.map((p) =>
    Math.atan2(
      p.y - position.y,
      Math.sin(yaw) * (p.x - position.x) + Math.cos(yaw) * (position.z - p.z),
    ),
  );
  const pitch = (Math.min(...pitches) + Math.max(...pitches)) / 2;
  const frame = {
    ...base,
    ...position,
    lookX: position.x + Math.sin(yaw) * Math.cos(pitch),
    lookY: position.y + Math.sin(pitch),
    lookZ: position.z - Math.cos(yaw) * Math.cos(pitch),
    near: 0.1,
    fov: aspect < 1 ? 64 : 58,
    up: { x: 0, y: 1, z: 0 },
  };
  const limit = aspect < 1 ? 110 : 100;
  for (const point of points)
    frame.fov = Math.max(frame.fov, Math.min(limit, pointFov(frame, point, aspect, 0.9)));
  const context = [
    ...t.members.map((m) => m.pose.sockets.reactor_target),
    t.tether.from,
    t.tether.to,
    t.tether.knot,
    t.repairLink?.from,
    t.repairLink?.to,
  ];
  for (const point of context.filter(Boolean)) {
    const required = pointFov(frame, point, aspect, 0.9);
    if (required <= limit) frame.fov = Math.max(frame.fov, required);
  }
  return frame;
}
